
// Imports ------------------------------------------------------------------->

import {inject} from "../services/di";
import {parseSearchParams} from "../utils/URL";
import {ErrorService} from "../services/ErrorService";
import {AggregateSourceAction} from "../services/definitions/IAnalyticsService";
import {FlyerDetails} from "../definitions/FlyerDefinitions";

// Private Definitions ------------------------------------------------------->

/**
 * Matches a path schema against a path and returns the results.
 * @param {string} schema The path schema to match
 * @param {string} fragment The path fragment to match against.
 */
function pathMatch(schema: string, fragment: string): {[param: string]: string} {
	// Remove trailing slashes from path fragment
	if (fragment !== "/")
		fragment = fragment.replace(/\/$/g, "");

	// extract the regexp and parameter names from the schema
	const namedParam = /:(\w+)/g;
	const escapeRegExp = /[\-{}\[\]+?.,\\\^$|#\s]/g;

	const schemaParams: string[] = [];
	const re = new RegExp("^" + schema
				.replace(escapeRegExp, "\\$&")
				.replace(namedParam, (match, name) => {
					schemaParams.push(name);
					return "([^/?]+)";
				}) + "$");

	// test the fragment and extract the parameter values
	const match = re.exec(fragment);
	if (!match) return null;

	const params: {[name: string]: string} = {};
	match.slice(1).forEach((val, i) => {
		params[schemaParams[i]] = decodeURIComponent(val);
	});

	return params;
}

// Public Definitions -------------------------------------------------------->

export type RequestDetails = {
	sourceAction?: AggregateSourceAction;
	context?: FlyerDetails;
};

export type ILocationInfo = {
	postal_code: string;
	city_name: string;
	country_code_by_postal_code?: string;
	country_code_by_ip?: string;
	region_name: string;
};

export class Request {
	path: string;
	params: { [name: string]: string } = {};
	details: RequestDetails;
	locationInfo: ILocationInfo | null = null;

	constructor(public url: URL, details?: RequestDetails) {
		this.path = this.url.pathname;
		this.params = parseSearchParams(url);
		this.details = details;
	}
}


export class Response {
	title: string | Promise<string>;
	description: string | Promise<string>;
	status: number;
	body: HTMLElement;
	headers: {[name: string]: string} = {};

	private _baseURL: URL;
	private _renderPromise: Promise<void>;
	private _renderPromiseResolver: {
		resolve: () => void,
		reject: () => void
	};

	constructor(baseURL?: URL) {
		this._baseURL = baseURL;

		this._renderPromise = new Promise<void>((resolve, reject) => {
			this._renderPromiseResolver = { resolve: resolve, reject: reject };
		});
	}

	setResponseHeader(name: string, value: string) {
		this.headers[name] = value;
	}

	redirect(url: string, code?: number) {
		if (code === undefined) code = 302;
		this.status = code;
		this.setResponseHeader("Location", new URL(url, this._baseURL.toString()).toString());
	}

	render(body: HTMLElement) {
		this.body = body;
		return this._renderPromise;
	}

	resolve() {
		this._renderPromiseResolver.resolve();
	}
}


export type Middleware = (req: Request, res: Response, next?: () => void|Promise<any>) => void|Promise<any>;
interface DefineOptions {
	name?: string;
	from?: string;
	to?: string;
}
type Routable = string|DefineOptions;

/**
 * The router class.
 */
export class Router {
	@inject error: ErrorService;

	private _stack: Middleware[] = [];
	private _controllers: { [name: string]: (string|Middleware)[] } = {};
	private _transitions: {[fromto: string]: (string|Middleware)[] } = {};
	private _history: string[] = [];
	private addedRoutes: { [name: string]: boolean } = {};

	constructor() {}

	/**
	 * Push a middleware to the stack. The middleware will be called in order,
	 * when handling a route.
	 * @param  {Middleware} middleware The middleware.
	 * @return {Router}                The router.
	 */
	use(middleware: Middleware): Router {
		this._stack.push(middleware);
		return this;
	}

	/**
	 * This method emulates route method behavior of matching route to params
	 * So the URL params from path are avaiable before running the middleware chain
	 */
	addRoute(path: string): Router {
		this._stack.push((req, res, next) => {
			this.addedRoutes[path] = this.patchParams(req, path);
			return next();
		});

		return this;
	}

	patchParams(req: Request, path: string): boolean {
		const matches = pathMatch(path, req.path);
		if (matches) {
			// Patch the parameter matches into the request.
			Object.getOwnPropertyNames(matches).forEach((name) => {
				req.params[name] = req.params[name] || matches[name];
			});
		}

		return Boolean(matches);
	}
	/**
	 * Pushes a URL matcher to the middleware stack.
	 * @param  {Routable} path               The string path or regexp to check.
	 * @param  {Middleware}    handler       The first middleware.
	 * @param  {Middleware[]}  ...middleware Optionally more middleware.
	 * @return {Router}                      The router.
	 */
	route(path: Routable, handler: string|Middleware, ...middleware: (string|Middleware)[]): Router {
		const middlewares = [handler].concat(middleware);

		if (typeof path === "string") {
			// For simplicity, a route is just a middleware that tests the request's
			// path...
			this._stack.push((req, res, next) => {
				const matches = typeof this.addedRoutes[path] !== "undefined" ? this.addedRoutes[path] : this.patchParams(req, path);

				if (matches) {
					// Call the route specific middleware
					return this._chain(middlewares)(req, res, next);
				} else {
					return next();
				}
			});
		} else if (path.from !== undefined && path.to !== undefined) {
			this._transitions[`[${path.from}]:[${path.to}]`] = middlewares;
		} else {
			throw new Error("Not Implemented.");
		}

		return this;
	}


	/**
	 * Define a named controller.
	 * @param  {string}               name          The name.
	 * @param  {Middleware}           handler       The first middleware.
	 * @param  {Middleware[]}         ...middleware Optionally more middleware.
	 * @return {Router}                             The router.
	 */
	define(name: string, handler: string|Middleware, ...middlewares: (string|Middleware)[]): Router {
		const handlers = [handler].concat(middlewares);
		this._controllers[name] = handlers;
		return this;
	}


	/**
	 * Handle navigation, could be from popstate or `navigate`.
	 * @param {string} url The URL to handle navigation to.
	 * @param {RequestDetails} details The details object containing additional parameters to be communicated.
	 */
	handleURL(url: URL, details?: RequestDetails) {
		// create the request, response
		const req = new Request(url, details);
		const res = new Response(url);

		return this._chain(this._stack)(req, res)
				.then(() => {
					return res;
				});
	}

	get currentRoute() {
		return this.history(0);
	}

	history(at: number) {
		const index = at <= 0 ? this._history.length - 1 + at : at;
		return this._history[index];
	}

	/**
	 * Chain a list of middleware into a single promise.
	 * @param {Middleware[]} middlewares The middlewares to chain.
	 */
	private _chain(middlewares: (string|Middleware)[]) {
		return (req: Request, res: Response, next?: () => void|Promise<any>) => {
			const clone = middlewares.slice(0);
			// bonkers CPS stuff, made especially weird by trying to maintain a
			// promise chain through it.
			const shiftMiddleware = (): void|Promise<any> => {
				const current = clone.shift();
				if (!current) {
					// no more middleware, call the next fn if provided
					// 404 if no next?
					return next ? next() : undefined;
				}

				if (typeof current === "string") {
					const transitionMiddleware = this.currentRoute ?
							this._transitions[`[${this.currentRoute}]:[${current}]`] || [] : [];

					const controllerMiddlewares = transitionMiddleware.concat(
							this._controllers[current] || []);

					this._history.push(current);
					return this._chain(controllerMiddlewares)(req, res, shiftMiddleware);
				} else {
					return current(req, res, () => {
						// continue this weird CPS bullshit
						return shiftMiddleware();
					});
				}
			};

			return Promise.resolve()
				.then(shiftMiddleware)
				.catch((e: Error) => {
					this.error.reportError(e);
				});
		};
	}
}
